// What every page of a client tab keeps doing in the background (#10718).
//
// Renders nothing. The client is named where the app already names the
// organization, at the foot of the sidebar (slice 3: no banner). This keeps
// the tab's title naming the client, re-reads identity when the grant runs
// out (the 401 that answers sends the tab to the "access ended" page), and
// re-reads it when another tab removes or replaces the token, which ends this
// page's access the same way (`clientTabToken`).
import React, { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CLIENT_ENVIRONMENT_TOKEN_KEY } from "@/services/clientEnvironment";
import { CURRENT_USER_QUERY_KEY } from "@/hooks/useCurrentUser";
import { useClientEnvironment } from "./clientEnvironmentAccess";
import { useMinutesLeft } from "./useMinutesLeft";
import { useClientEnvironmentTitle } from "./useClientEnvironmentTitle";

const ClientEnvironmentKeeper: React.FC = () => {
  const environment = useClientEnvironment();
  if (environment === null) return null;
  return (
    <Keep
      client={environment.organizationName}
      expiresAt={environment.expiresAt}
    />
  );
};

const Keep: React.FC<{
  client: string;
  expiresAt: string;
}> = ({ client, expiresAt }) => {
  const queryClient = useQueryClient();
  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY }),
    [queryClient]
  );

  useClientEnvironmentTitle(client);

  // The server stops honouring the token when the grant lapses.
  const minutes = useMinutesLeft(expiresAt);
  useEffect(() => {
    if (minutes <= 0) void refresh();
  }, [minutes, refresh]);

  // This browser's client tabs share one token. When another tab removes
  // it (ending access) or replaces it (opening a client), this page no
  // longer has a credential: identity is re-read, finds none, and the ended
  // page shows. It never follows the new token into another client.
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (
        event.key === CLIENT_ENVIRONMENT_TOKEN_KEY &&
        event.newValue !== event.oldValue
      ) {
        void refresh();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refresh]);

  return null;
};

export default ClientEnvironmentKeeper;
